import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { CalendarDays, Sparkles, Info } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAppStore } from "@/lib/store";
import { buildSeo } from "@/lib/seo";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { gregorianToBangla, formatBanglaDate } from "@/lib/bengaliDate";
import { HIJRI_MONTHS } from "@/data/hijriMonths";
import {
  gregToHijri,
  hijriToGreg,
  upcomingEvents,
  eventsForHijriYear,
  eventsOnGregorianDate,
  WEEKDAYS_BN,
  WEEKDAYS_EN,
} from "@/lib/calculators/hijriConverter";

type Bilingual = { en: string; bn: string };

const STR = {
  h1: { en: "Hijri Date Converter", bn: "হিজরি তারিখ কনভার্টার" },
  desc: {
    en: "Convert between Gregorian, Hijri, and Bengali (Bangla shon) calendars. Today's date in all three plus a full Islamic events calendar.",
    bn: "গ্রেগরিয়ান, হিজরি ও বঙ্গাব্দ — তিন ক্যালেন্ডারে রূপান্তর। আজকের তারিখ ও ইসলামিক ইভেন্ট ক্যালেন্ডার।",
  },
  today: { en: "Today", bn: "আজ" },
  g2h: { en: "Gregorian → Hijri", bn: "গ্রেগরিয়ান → হিজরি" },
  h2g: { en: "Hijri → Gregorian", bn: "হিজরি → গ্রেগরিয়ান" },
  events: { en: "Islamic Events", bn: "ইসলামিক ইভেন্ট" },
  weekday: { en: "Day", bn: "দিন" },
  gregorian: { en: "Gregorian", bn: "গ্রেগরিয়ান" },
  hijri: { en: "Hijri", bn: "হিজরি" },
  bangla: { en: "Bangla shon (BS)", bn: "বঙ্গাব্দ" },
  upcoming: { en: "Upcoming Islamic events", bn: "আসন্ন ইসলামিক ইভেন্ট" },
  inDays: { en: "in %s days", bn: "%s দিন বাকি" },
  today2: { en: "today", bn: "আজ" },
  pickDate: { en: "Pick a Gregorian date", bn: "গ্রেগরিয়ান তারিখ বাছুন" },
  hijriDay: { en: "Hijri day", bn: "হিজরি দিন" },
  hijriMonth: { en: "Hijri month", bn: "হিজরি মাস" },
  hijriYear: { en: "Hijri year", bn: "হিজরি বছর" },
  result: { en: "Result", bn: "ফলাফল" },
  yearLabel: { en: "Hijri year", bn: "হিজরি বছর" },
  eventCol: { en: "Event", bn: "ইভেন্ট" },
  hijriCol: { en: "Hijri", bn: "হিজরি" },
  gregCol: { en: "Gregorian", bn: "গ্রেগরিয়ান" },
  bnCol: { en: "Bengali", bn: "বঙ্গাব্দ" },
  disclaimer: {
    en: "Hijri dates depend on local moon sighting. The Bangladesh National Moon Sighting Committee (Islamic Foundation) announces official dates. Calendar-based calculation may differ by ±1 day.",
    bn: "হিজরি তারিখ চাঁদ দেখার উপর নির্ভর করে। বাংলাদেশের জাতীয় চাঁদ দেখা কমিটি (ইসলামিক ফাউন্ডেশন) আনুষ্ঠানিক তারিখ ঘোষণা করে। আমাদের গণনা ক্যালেন্ডার-ভিত্তিক, ১ দিন আগ-পিছ হতে পারে।",
  },
  significance: { en: "Significance", bn: "তাৎপর্য" },
  noEvent: { en: "No major Islamic event on this day.", bn: "এই দিনে কোনো বড় ইসলামিক ইভেন্ট নেই।" },
};

const FAQ: { q: Bilingual; a: Bilingual }[] = [
  { q: { en: "What is today's Hijri date?", bn: "আজকের হিজরি তারিখ কত?" }, a: { en: "Open the 'Today' tab — it shows the current Hijri date along with Gregorian and Bangla shon dates.", bn: "'আজ' ট্যাব খুলুন — সেখানে আজকের হিজরি, গ্রেগরিয়ান ও বঙ্গাব্দ তারিখ দেখা যাবে।" } },
  { q: { en: "When does Ramadan start?", bn: "রমাদান কখন শুরু হবে?" }, a: { en: "Ramadan begins on 1 Ramadan in the Hijri calendar. Use the Events tab and pick the Hijri year — actual start in Bangladesh depends on moon sighting.", bn: "১ রমাদানে শুরু হয়। ইভেন্ট ট্যাবে হিজরি বছর বাছাই করুন — বাংলাদেশে প্রকৃত শুরু চাঁদ দেখার উপর নির্ভর করে।" } },
  { q: { en: "How many months are in the Hijri calendar?", bn: "হিজরি ক্যালেন্ডারে কত মাস?" }, a: { en: "12 lunar months (Muharram to Dhul-Hijjah), totalling about 354–355 days per year.", bn: "১২টি চান্দ্র মাস (মুহাররম থেকে যিলহজ), মোট প্রায় ৩৫৪–৩৫৫ দিন।" } },
  { q: { en: "Who announces moon sighting in Bangladesh?", bn: "বাংলাদেশে চাঁদ দেখা কে ঘোষণা করে?" }, a: { en: "The National Moon Sighting Committee under Islamic Foundation Bangladesh, based on testimony from across the country.", bn: "ইসলামিক ফাউন্ডেশন বাংলাদেশের অধীনে জাতীয় চাঁদ দেখা কমিটি দেশজুড়ে সাক্ষ্যের ভিত্তিতে ঘোষণা করে।" } },
  { q: { en: "Why is the Hijri year shorter than Gregorian?", bn: "হিজরি বছর গ্রেগরিয়ানের চেয়ে ছোট কেন?" }, a: { en: "The Hijri calendar is purely lunar (~354.37 days) while Gregorian is solar (~365.25 days), so Hijri is about 10–11 days shorter every year.", bn: "হিজরি সম্পূর্ণ চান্দ্র (~৩৫৪.৩৭ দিন) আর গ্রেগরিয়ান সৌর (~৩৬৫.২৫ দিন), তাই হিজরি বছর ১০–১১ দিন ছোট।" } },
  { q: { en: "What is the difference between Hijri and Bangla shon?", bn: "হিজরি ও বঙ্গাব্দে পার্থক্য কী?" }, a: { en: "Bangla shon (BS) is the Bangladeshi solar calendar starting 14 April; Hijri is the Islamic lunar calendar. Both started near 593–622 CE but track different cycles.", bn: "বঙ্গাব্দ বাংলাদেশের সৌর ক্যালেন্ডার, ১৪ এপ্রিল থেকে শুরু; হিজরি ইসলামি চান্দ্র ক্যালেন্ডার। দু'টি প্রায় কাছাকাছি সময়ে শুরু হলেও আলাদা চক্র অনুসরণ করে।" } },
  { q: { en: "How accurate is calendar-based Hijri date?", bn: "ক্যালেন্ডার-ভিত্তিক হিজরি তারিখ কতটা সঠিক?" }, a: { en: "We use the Umm al-Qura tabular calendar — accurate within ±1 day of the actual moon-sighted date in Bangladesh.", bn: "আমরা উম্মুল কুরা ছকভিত্তিক ক্যালেন্ডার ব্যবহার করি — বাংলাদেশের প্রকৃত চাঁদ দেখা তারিখ থেকে ±১ দিনের মধ্যে।" } },
  { q: { en: "When did the Hijri calendar start?", bn: "হিজরি ক্যালেন্ডার কবে শুরু?" }, a: { en: "Caliph Umar (RA) established it in 638 CE, with Year 1 set to 622 CE — the year of the Prophet's (PBUH) Hijra (migration) to Madinah.", bn: "খলিফা উমর (রা.) ৬৩৮ খ্রিস্টাব্দে প্রবর্তন করেন; ১ম বছর ধরা হয় ৬২২ খ্রিস্টাব্দ — মহানবী (সা.)-এর হিজরতের বছর।" } },
  { q: { en: "Is Mawlid date the same worldwide?", bn: "মিলাদুন্নবীর তারিখ সব দেশে এক?" }, a: { en: "12 Rabi' al-Awwal in the Hijri calendar is the same Hijri date worldwide, but the corresponding Gregorian date can shift by 1 day across regions.", bn: "হিজরি ১২ রবিউল আউয়াল সব দেশে একই, তবে অনুরূপ গ্রেগরিয়ান তারিখ অঞ্চলভেদে ১ দিন ভিন্ন হতে পারে।" } },
  { q: { en: "Can I plan Hajj dates with this tool?", bn: "এই টুল দিয়ে হজের তারিখ পরিকল্পনা করা যাবে?" }, a: { en: "Yes for rough planning — Hajj is 8–13 Dhul-Hijjah. For ticket booking, always confirm with official Saudi announcements.", bn: "প্রাথমিক পরিকল্পনায় হ্যাঁ — হজ ৮–১৩ যিলহজ। টিকিট বুকিংয়ের জন্য সৌদি কর্তৃপক্ষের আনুষ্ঠানিক ঘোষণা মিলিয়ে নিন।" } },
];

export const Route = createFileRoute("/hijri-date-converter")({
  head: () => {
    const seo = buildSeo({
      title: "হিজরি তারিখ আজ — Hijri Date Converter Bangladesh",
      description:
        "আজকের হিজরি তারিখ। গ্রেগরিয়ান, হিজরি, বঙ্গাব্দ — তিনটি ক্যালেন্ডারে রূপান্তর। ইসলামিক ইভেন্ট ক্যালেন্ডার। Hijri date today, three-way calendar converter for Bangladesh.",
      path: "/hijri-date-converter",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Hijri Date Converter", path: "/hijri-date-converter" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: HijriPage,
});

function pad2(n: number) { return n < 10 ? `0${n}` : `${n}`; }
function toInputDate(d: Date) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function HijriPage() {
  const lang = useAppStore((s) => s.lang);
  const setLang = useAppStore((s) => s.setLang);
  const t = (k: Bilingual) => k[lang];
  const num = (n: number | string) => (lang === "bn" ? toBengaliNumerals(n) : String(n));

  useEffect(() => {
    if (!useAppStore.getState().langTouched) setLang("bn");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [tab, setTab] = useState("today");
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  // Tab 2 — gregorian input
  const [gDate, setGDate] = useState<Date>(new Date());
  // Tab 3 — hijri input
  const todayH = gregToHijri(new Date());
  const [hDay, setHDay] = useState(todayH.hd);
  const [hMonth, setHMonth] = useState(todayH.hm);
  const [hYear, setHYear] = useState(todayH.hy);
  // Tab 4 — events year
  const [evYear, setEvYear] = useState(todayH.hy);

  const todayGreg = now;
  const todayHijri = gregToHijri(todayGreg);
  const todayBangla = gregorianToBangla(todayGreg);
  const todayWeekday = lang === "bn" ? WEEKDAYS_BN[todayGreg.getDay()] : WEEKDAYS_EN[todayGreg.getDay()];
  const upcoming = useMemo(() => upcomingEvents(todayGreg, 6), [todayGreg.toDateString()]); // eslint-disable-line react-hooks/exhaustive-deps

  // g2h result
  const g2hHijri = useMemo(() => gregToHijri(gDate), [gDate]);
  const g2hBangla = useMemo(() => gregorianToBangla(gDate), [gDate]);
  const g2hEvents = useMemo(() => eventsOnGregorianDate(gDate), [gDate]);
  const g2hWeekday = lang === "bn" ? WEEKDAYS_BN[gDate.getDay()] : WEEKDAYS_EN[gDate.getDay()];

  // h2g result
  const h2gResult = useMemo(() => {
    try {
      const d = hijriToGreg(hYear, hMonth, hDay);
      return { date: d, error: null as string | null };
    } catch (e: unknown) {
      return { date: null, error: (e as Error).message };
    }
  }, [hDay, hMonth, hYear]);
  const h2gBangla = h2gResult.date ? gregorianToBangla(h2gResult.date) : null;
  const h2gWeekday = h2gResult.date
    ? (lang === "bn" ? WEEKDAYS_BN[h2gResult.date.getDay()] : WEEKDAYS_EN[h2gResult.date.getDay()])
    : "";

  const evList = useMemo(() => eventsForHijriYear(evYear), [evYear]);

  const monthName = (idx: number) => {
    const m = HIJRI_MONTHS[idx - 1];
    return lang === "bn" ? m.bn : m.en;
  };

  const fmtGreg = (d: Date) =>
    d.toLocaleDateString(lang === "bn" ? "bn-BD" : "en-GB", {
      day: "numeric", month: "long", year: "numeric",
    });

  return (
    <CalculatorLayout
      slug="hijri-date-converter"
      title={t(STR.h1)}
      description={t(STR.desc)}
      iconName="CalendarDays"
      faq={FAQ.map((f) => ({ q: t(f.q), a: t(f.a) }))}
    >
      {/* Disclaimer banner */}
      <div className={`mb-6 flex gap-3 rounded-xl border border-accent/20 bg-accent/5 p-4 text-sm ${lang === "bn" ? "font-bn" : ""}`}>
        <Info size={18} className="mt-0.5 shrink-0 text-accent" />
        <p className="text-muted-foreground">{t(STR.disclaimer)}</p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4">
          <TabsTrigger value="today" className={lang === "bn" ? "font-bn" : ""}>{t(STR.today)}</TabsTrigger>
          <TabsTrigger value="g2h" className={lang === "bn" ? "font-bn" : ""}>{t(STR.g2h)}</TabsTrigger>
          <TabsTrigger value="h2g" className={lang === "bn" ? "font-bn" : ""}>{t(STR.h2g)}</TabsTrigger>
          <TabsTrigger value="events" className={lang === "bn" ? "font-bn" : ""}>{t(STR.events)}</TabsTrigger>
        </TabsList>

        {/* Tab 1: Today */}
        <TabsContent value="today" className="mt-6">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border bg-gradient-to-br from-accent/10 via-card to-card p-6 shadow-sm"
          >
            <div className={`mb-4 flex items-center gap-2 text-sm text-muted-foreground ${lang === "bn" ? "font-bn" : ""}`}>
              <CalendarDays size={16} /> {t(STR.today)} • {todayWeekday}
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <DateCard label={t(STR.gregorian)} primary={fmtGreg(todayGreg)} sub="" lang={lang} />
              <DateCard
                label={t(STR.hijri)}
                primary={`${num(todayHijri.hd)} ${monthName(todayHijri.hm)} ${num(todayHijri.hy)}`}
                sub="AH"
                lang={lang}
              />
              <DateCard
                label={t(STR.bangla)}
                primary={formatBanglaDate(todayBangla, lang)}
                sub="BS"
                lang={lang}
              />
            </div>
          </motion.div>

          <div className="mt-8">
            <h2 className={`mb-3 flex items-center gap-2 text-lg font-semibold ${lang === "bn" ? "font-bn" : ""}`}>
              <Sparkles size={18} className="text-accent" /> {t(STR.upcoming)}
            </h2>
            <ul className="space-y-2">
              {upcoming.map((u) => (
                <li
                  key={`${u.event.key}-${u.hijriYear}`}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-xl border bg-card p-3 text-sm"
                >
                  <div className={lang === "bn" ? "font-bn" : ""}>
                    <div className="font-medium">{lang === "bn" ? u.event.name.bn : u.event.name.en}</div>
                    <div className="text-xs text-muted-foreground">
                      {num(u.event.hDay)} {monthName(u.event.hMonth)} {num(u.hijriYear)} • {fmtGreg(u.date)}
                    </div>
                  </div>
                  <span className={`rounded-full bg-accent/10 px-3 py-1 text-xs font-medium text-accent ${lang === "bn" ? "font-bn" : ""}`}>
                    {u.daysAway === 0 ? t(STR.today2) : t(STR.inDays).replace("%s", num(u.daysAway))}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </TabsContent>

        {/* Tab 2: Gregorian → Hijri */}
        <TabsContent value="g2h" className="mt-6 space-y-6">
          <div>
            <label className={`mb-1 block text-sm font-medium ${lang === "bn" ? "font-bn" : ""}`}>{t(STR.pickDate)}</label>
            <Input
              type="date"
              value={toInputDate(gDate)}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                const [y, m, d] = v.split("-").map(Number);
                setGDate(new Date(y, m - 1, d));
              }}
              className="max-w-xs"
            />
          </div>
          <div className="rounded-2xl border bg-card p-5">
            <div className={`mb-3 text-xs uppercase tracking-wide text-muted-foreground ${lang === "bn" ? "font-bn" : ""}`}>{t(STR.result)}</div>
            <div className="grid gap-3 sm:grid-cols-3">
              <ResultRow label={t(STR.hijri)} value={`${num(g2hHijri.hd)} ${monthName(g2hHijri.hm)} ${num(g2hHijri.hy)} AH`} lang={lang} />
              <ResultRow label={t(STR.bangla)} value={`${formatBanglaDate(g2hBangla, lang)} BS`} lang={lang} />
              <ResultRow label={t(STR.weekday)} value={g2hWeekday} lang={lang} />
            </div>
            <div className={`mt-4 rounded-lg bg-muted/50 p-3 text-sm ${lang === "bn" ? "font-bn" : ""}`}>
              {g2hEvents.length === 0 ? (
                <span className="text-muted-foreground">{t(STR.noEvent)}</span>
              ) : (
                <ul className="space-y-1">
                  {g2hEvents.map((u) => (
                    <li key={u.event.key}>
                      <strong>★ {lang === "bn" ? u.event.name.bn : u.event.name.en}</strong>
                      <span className="text-muted-foreground"> — {lang === "bn" ? u.event.description.bn : u.event.description.en}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Tab 3: Hijri → Gregorian */}
        <TabsContent value="h2g" className="mt-6 space-y-6">
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className={`mb-1 block text-sm font-medium ${lang === "bn" ? "font-bn" : ""}`}>{t(STR.hijriDay)}</label>
              <Input type="number" min={1} max={30} value={hDay} onChange={(e) => setHDay(Math.max(1, Math.min(30, Number(e.target.value) || 1)))} />
            </div>
            <div>
              <label className={`mb-1 block text-sm font-medium ${lang === "bn" ? "font-bn" : ""}`}>{t(STR.hijriMonth)}</label>
              <Select value={String(hMonth)} onValueChange={(v) => setHMonth(Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {HIJRI_MONTHS.map((m) => (
                    <SelectItem key={m.index} value={String(m.index)}>
                      {m.index}. {lang === "bn" ? m.bn : m.en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={`mb-1 block text-sm font-medium ${lang === "bn" ? "font-bn" : ""}`}>{t(STR.hijriYear)}</label>
              <Input type="number" min={1318} max={1500} value={hYear} onChange={(e) => setHYear(Number(e.target.value) || todayH.hy)} />
            </div>
          </div>
          <div className="rounded-2xl border bg-card p-5">
            <div className={`mb-3 text-xs uppercase tracking-wide text-muted-foreground ${lang === "bn" ? "font-bn" : ""}`}>{t(STR.result)}</div>
            {h2gResult.error || !h2gResult.date ? (
              <p className="text-sm text-destructive">Invalid Hijri date</p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-3">
                <ResultRow label={t(STR.gregorian)} value={fmtGreg(h2gResult.date)} lang={lang} />
                <ResultRow label={t(STR.bangla)} value={`${formatBanglaDate(h2gBangla!, lang)} BS`} lang={lang} />
                <ResultRow label={t(STR.weekday)} value={h2gWeekday} lang={lang} />
              </div>
            )}
          </div>
        </TabsContent>

        {/* Tab 4: Events */}
        <TabsContent value="events" className="mt-6 space-y-4">
          <div className="flex items-end gap-3">
            <div>
              <label className={`mb-1 block text-sm font-medium ${lang === "bn" ? "font-bn" : ""}`}>{t(STR.yearLabel)}</label>
              <Input type="number" value={evYear} min={1318} max={1500} onChange={(e) => setEvYear(Number(e.target.value) || todayH.hy)} className="w-32" />
            </div>
            <Button variant="outline" size="sm" onClick={() => setEvYear(todayH.hy)}>
              {todayH.hy}
            </Button>
          </div>
          <div className="overflow-x-auto rounded-2xl border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className={`p-3 ${lang === "bn" ? "font-bn" : ""}`}>{t(STR.eventCol)}</th>
                  <th className={`p-3 ${lang === "bn" ? "font-bn" : ""}`}>{t(STR.hijriCol)}</th>
                  <th className={`p-3 ${lang === "bn" ? "font-bn" : ""}`}>{t(STR.gregCol)}</th>
                  <th className={`p-3 ${lang === "bn" ? "font-bn" : ""}`}>{t(STR.bnCol)}</th>
                </tr>
              </thead>
              <tbody>
                {evList.map((u) => {
                  const ban = gregorianToBangla(u.date);
                  return (
                    <tr key={u.event.key} className="border-t">
                      <td className={`p-3 font-medium ${lang === "bn" ? "font-bn" : ""}`}>
                        {lang === "bn" ? u.event.name.bn : u.event.name.en}
                      </td>
                      <td className={`p-3 ${lang === "bn" ? "font-bn" : ""}`}>
                        {num(u.event.hDay)} {monthName(u.event.hMonth)} {num(u.hijriYear)}
                      </td>
                      <td className={`p-3 ${lang === "bn" ? "font-bn" : ""}`}>{fmtGreg(u.date)}</td>
                      <td className={`p-3 ${lang === "bn" ? "font-bn" : ""}`}>{formatBanglaDate(ban, lang)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className={`text-xs text-muted-foreground ${lang === "bn" ? "font-bn" : ""}`}>{t(STR.disclaimer)}</p>
        </TabsContent>
      </Tabs>

      {/* Educational content */}
      <section className={`mt-12 space-y-8 ${lang === "bn" ? "font-bn" : ""}`}>
        <Article
          lang={lang}
          title={{ en: "What is the Hijri calendar?", bn: "হিজরি ক্যালেন্ডার কী?" }}
          body={{
            en: "The Hijri (Islamic) calendar is a purely lunar calendar of 12 months and ~354 days per year. It was established by Caliph Umar (RA) in 638 CE, with Year 1 set to 622 CE — the year of the Prophet Muhammad's (PBUH) Hijra (migration) from Makkah to Madinah.",
            bn: "হিজরি (ইসলামি) ক্যালেন্ডার সম্পূর্ণ চান্দ্র — ১২ মাস ও বছরে প্রায় ৩৫৪ দিন। ৬৩৮ খ্রিস্টাব্দে খলিফা উমর (রা.) এটি প্রবর্তন করেন; ১ম বছর ৬২২ খ্রিস্টাব্দ — মহানবী (সা.)-এর মক্কা থেকে মদিনা হিজরতের বছর।",
          }}
        />

        <div>
          <h2 className="text-xl font-semibold">{lang === "bn" ? "হিজরি মাসসমূহ" : "Hijri months"}</h2>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {HIJRI_MONTHS.map((m) => (
              <div key={m.index} className="rounded-xl border bg-card p-3 text-sm">
                <div className="flex items-baseline justify-between">
                  <span className="font-medium">{m.index}. {lang === "bn" ? m.bn : m.en}</span>
                  <span className="font-arabic text-base text-muted-foreground" dir="rtl">{m.arabic}</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {lang === "bn" ? m.significance.bn : m.significance.en}
                </p>
              </div>
            ))}
          </div>
        </div>

        <Article
          lang={lang}
          title={{ en: "Moon sighting in Bangladesh", bn: "চাঁদ দেখা ও বাংলাদেশ" }}
          body={{
            en: "In Bangladesh, the National Moon Sighting Committee (Jatiya Chand Dekha Committee) under Islamic Foundation Bangladesh meets at the end of Sha'ban, Ramadan, and Dhul-Qi'dah to confirm the new crescent. Their announcement determines the official start of Ramadan, Eid-ul-Fitr, and Eid-ul-Adha — which may differ by ±1 day from purely calendar-based dates.",
            bn: "বাংলাদেশে ইসলামিক ফাউন্ডেশনের অধীনে জাতীয় চাঁদ দেখা কমিটি শাবান, রমাদান ও যিলক্বদের শেষে নতুন চাঁদ নিশ্চিত করতে বৈঠকে বসে। তাদের ঘোষণার ভিত্তিতে রমাদান, ঈদুল ফিতর ও ঈদুল আযহার আনুষ্ঠানিক তারিখ নির্ধারিত হয় — যা ক্যালেন্ডার-ভিত্তিক তারিখ থেকে ±১ দিন ভিন্ন হতে পারে।",
          }}
        />

        <Article
          lang={lang}
          title={{ en: "Hijri vs Gregorian — why ~10 days shorter?", bn: "হিজরি বনাম গ্রেগরিয়ান — কেন ~১০ দিন ছোট?" }}
          body={{
            en: "A Hijri lunar year is ~354.37 days while the Gregorian solar year is ~365.25 days. The 10–11 day annual gap is why Ramadan and Eid shift earlier across Gregorian seasons over the years — Ramadan cycles through every season roughly every 33 Gregorian years.",
            bn: "হিজরি চান্দ্র বছর প্রায় ৩৫৪.৩৭ দিন, গ্রেগরিয়ান সৌর বছর প্রায় ৩৬৫.২৫ দিন। বছরে ১০–১১ দিনের পার্থক্যের কারণে রমাদান ও ঈদ গ্রেগরিয়ান ঋতুর মধ্যে ক্রমে এগিয়ে আসে — প্রায় ৩৩ বছরে রমাদান সব ঋতু ঘুরে আসে।",
          }}
        />

        <Article
          lang={lang}
          title={{ en: "Bangla shon (BS) — Bengali calendar", bn: "বঙ্গাব্দ — বাংলা ক্যালেন্ডার" }}
          body={{
            en: "Bangla shon is the Bangladeshi solar calendar, starting on 14 April (Pahela Boishakh). It was reformed in 1966 by Dr. Muhammad Shahidullah and officially adopted by Bangladesh in 1987. Year 1 is set near 593 CE. See the Year Calculator for more.",
            bn: "বঙ্গাব্দ বাংলাদেশের সৌর ক্যালেন্ডার, ১৪ এপ্রিল (পহেলা বৈশাখ) থেকে শুরু। ড. মুহাম্মদ শহীদুল্লাহ ১৯৬৬ সালে এটি সংস্কার করেন এবং বাংলাদেশ ১৯৮৭ সালে আনুষ্ঠানিকভাবে গ্রহণ করে। ১ম বছর প্রায় ৫৯৩ খ্রিস্টাব্দ। বিস্তারিত Year Calculator-এ।",
          }}
        />

        <div className="rounded-xl border bg-accent/5 p-4 text-sm">
          <p className={lang === "bn" ? "font-bn" : ""}>
            {lang === "bn" ? "সম্পর্কিত: " : "Related: "}
            <Link to="/prayer-times" className="underline">{lang === "bn" ? "নামাজের সময়" : "Prayer Times"}</Link>
            {" · "}
            <Link to="/sehri-iftar-time" className="underline">{lang === "bn" ? "সেহরি ও ইফতার" : "Sehri & Iftar"}</Link>
            {" · "}
            <Link to="/zakat-calculator" className="underline">{lang === "bn" ? "যাকাত" : "Zakat"}</Link>
            {" · "}
            <Link to="/year-calculator" className="underline">{lang === "bn" ? "বছর ক্যালকুলেটর" : "Year Calculator"}</Link>
          </p>
        </div>
      </section>
    </CalculatorLayout>
  );
}

function DateCard({ label, primary, sub, lang }: { label: string; primary: string; sub: string; lang: "en" | "bn" }) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <div className={`text-xs uppercase tracking-wide text-muted-foreground ${lang === "bn" ? "font-bn" : ""}`}>{label}</div>
      <div className={`mt-1 text-lg font-semibold leading-snug ${lang === "bn" ? "font-bn" : ""}`}>{primary}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function ResultRow({ label, value, lang }: { label: string; value: string; lang: "en" | "bn" }) {
  return (
    <div>
      <div className={`text-xs text-muted-foreground ${lang === "bn" ? "font-bn" : ""}`}>{label}</div>
      <div className={`text-base font-medium ${lang === "bn" ? "font-bn" : ""}`}>{value}</div>
    </div>
  );
}

function Article({ title, body, lang }: { title: Bilingual; body: Bilingual; lang: "en" | "bn" }) {
  return (
    <div>
      <h2 className="text-xl font-semibold">{lang === "bn" ? title.bn : title.en}</h2>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
        {lang === "bn" ? body.bn : body.en}
      </p>
    </div>
  );
}
